// API client using fetch to avoid axios Mixed-Content issues
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL.replace(/^http:\/\//i, 'https://');
const API_URL = `${BACKEND_URL}/api`;

function getHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function request(method, path, body = null, params = null) {
  let url = `${API_URL}${path}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) searchParams.append(k, v);
    });
    const qs = searchParams.toString();
    if (qs) url += `?${qs}`;
  }

  const options = { method, headers: getHeaders() };
  if (body && method !== 'GET') {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);

  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.detail || 'Request failed');
    error.response = { status: response.status, data };
    throw error;
  }

  return { data, status: response.status };
}

export const authAPI = {
  register: (body) => request('POST', '/auth/register', body),
  login: (body) => request('POST', '/auth/login', body),
  getMe: () => request('GET', '/auth/me'),
};

export const tipsAPI = {
  getTips: (params) => request('GET', '/tips/', null, params),
  getMyTips: () => request('GET', '/tips/my-tips'),
  getTipDetail: (id) => request('GET', `/tips/${id}`),
  purchaseTip: (id) => request('POST', `/tips/purchase/${id}`),
  generateTips: () => request('POST', '/tips/generate'),
};

export const subscriptionsAPI = {
  getTiers: () => request('GET', '/subscriptions/tiers'),
  createCheckout: (tier) => request('POST', '/subscriptions/checkout', { tier }),
  getMySubscription: () => request('GET', '/subscriptions/my-subscription'),
};

export const sportsAPI = {
  getAllSports: () => request('GET', '/sports'),
  getLeagues: (sport) => request('GET', '/sports/leagues', null, { sport }),
  getMatches: (sport, league) => request('GET', '/sports/matches', null, { sport, league }),
};

export const profileAPI = {
  getFull: () => request('GET', '/profile/me'),
  update: (data) => request('PUT', '/profile/me', data),
  getStreak: () => request('GET', '/profile/streak'),
  getAnalytics: () => request('GET', '/profile/analytics'),
};

export const referralAPI = {
  getCode: () => request('GET', '/referral/code'),
  apply: (code) => request('POST', '/referral/apply', { code }),
};

export const oddsAPI = {
  getSports: () => request('GET', '/odds/sports'),
  getLive: (sport) => request('GET', `/odds/live/${sport}`),
};

export const leaderboardAPI = {
  getMonthly: (month) => request('GET', '/leaderboard/monthly', null, month ? { month } : {}),
  getAllTime: () => request('GET', '/leaderboard/all-time'),
};

export const challengeAPI = {
  create: (opponent_code, challenge_type = 'standard') => request('POST', '/challenges/create', { opponent_code, challenge_type }),
  respond: (id, accept) => request('POST', `/challenges/${id}/respond`, { accept }),
  getMy: (status) => request('GET', '/challenges/', null, status ? { status } : {}),
  getDetail: (id) => request('GET', `/challenges/${id}`),
  getTypes: () => request('GET', '/challenges/types'),
};

export const supportAPI = {
  ask: (message, category, session_id) => request('POST', '/support/ask', { message, category, session_id }),
  history: () => request('GET', '/support/history'),
  rate: (session_id, rating) => request('POST', `/support/rate/${session_id}`, { rating }),
};

export const feedAPI = {
  getFeed: (limit = 30) => request('GET', '/feed/', null, { limit }),
  getHotTippers: () => request('GET', '/feed/hot-tippers'),
  react: (feed_key, reaction) => request('POST', '/feed/react', { feed_key, reaction }),
  getReactions: (feed_key) => request('GET', `/feed/reactions/${encodeURIComponent(feed_key)}`),
  comment: (feed_key, text) => request('POST', '/feed/comment', { feed_key, text }),
  getComments: (feed_key) => request('GET', `/feed/comments/${encodeURIComponent(feed_key)}`),
};

export const abTestAPI = {
  getVariant: (experiment) => request('GET', `/ab/variant/${experiment}`),
  track: (experiment, event) => request('POST', '/ab/track', { experiment, event }),
};

export const aiCouncilAPI = {
  analyze: (data) => request('POST', '/ai-council/analyze', data),
  history: () => request('GET', '/ai-council/history'),
  stats: () => request('GET', '/ai-council/stats'),
};

export const historicalDataAPI = {
  status: () => request('GET', '/historical-data/status'),
  trigger: () => request('POST', '/historical-data/trigger'),
  h2h: (home, away) => request('GET', `/historical-data/h2h/${encodeURIComponent(home)}/${encodeURIComponent(away)}`),
  form: (team) => request('GET', `/historical-data/form/${encodeURIComponent(team)}`),
};

export const digestAPI = {
  latest: () => request('GET', '/digest/latest'),
};

export const tipSharingAPI = {
  share: (tipId, comment) => request('POST', '/tip-sharing/share', { tip_id: tipId, comment }),
  community: (sort = 'recent', limit = 30) => request('GET', `/tip-sharing/community?sort=${sort}&limit=${limit}`),
  vote: (sharedTipId, vote) => request('POST', '/tip-sharing/vote', { shared_tip_id: sharedTipId, vote }),
  leaderboard: () => request('GET', '/tip-sharing/leaderboard'),
};

export const predictionsAPI = {
  predict: (homeTeam, awayTeam, sport, league) =>
    request('POST', '/predictions/predict', { home_team: homeTeam, away_team: awayTeam, sport, league }),
  modelInfo: () => request('GET', '/predictions/model-info'),
};

export const liveOddsAPI = {
  snapshot: (sport) => request('GET', `/live/snapshot/${sport}`),
};

export const socialAPI = {
  follow: (targetUserId) => request('POST', '/social/follow', { target_user_id: targetUserId }),
  unfollow: (targetUserId) => request('POST', '/social/unfollow', { target_user_id: targetUserId }),
  following: () => request('GET', '/social/following'),
  followersCount: (userId) => request('GET', `/social/followers-count/${userId}`),
  followingFeed: () => request('GET', '/social/feed'),
  tipsterOfTheDay: () => request('GET', '/social/tipster-of-the-day'),
  sendMessage: (toUserId, text) => request('POST', '/social/messages/send', { to_user_id: toUserId, text }),
  inbox: () => request('GET', '/social/messages/inbox'),
  conversation: (userId) => request('GET', `/social/messages/conversation/${userId}`),
  unreadCount: () => request('GET', '/social/messages/unread-count'),
};

// Backward-compatible default export with .get() and .post()
const api = {
  request,
  get: (path, config) => request('GET', path, null, config?.params),
  post: (path, body) => request('POST', path, body),
  put: (path, body) => request('PUT', path, body),
  delete: (path) => request('DELETE', path),
};

export default api;
